import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined';
import DeleteSweepOutlinedIcon from '@mui/icons-material/DeleteSweepOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import LocalFireDepartmentOutlinedIcon from '@mui/icons-material/LocalFireDepartmentOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import RecyclingOutlinedIcon from '@mui/icons-material/RecyclingOutlined';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Alert, Box, Button, Chip, CircularProgress, Grid,
  MenuItem, Paper, Select, Stack, Tooltip, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  createDataImportDownloadLink,
} from '../../../api/dataInputApi';
import useRole from '../../../auth/useRole';
import { usePagePresentation } from '../../../context/PagePresentationContext';
import { ApiError, translateMessage } from '../../../shared/api';
import AppToast from '../../../shared/components/AppToast';
import DataState from '../../../shared/components/DataState';
import LabeledField from '../../../shared/components/LabeledField';
import { SCOPE3_AF_ERROR_TOKENS } from '../../../shared/constants';
import useBeforeUnloadWarning from '../../../shared/hooks/useBeforeUnloadWarning';
import { triggerBrowserDownload } from '../../../shared/utils/download';
import { formatDateTimeJst } from '../../../shared/utils/format';
import { isHistoricalViewerYear } from '../../../shared/utils/reportingYear';
import {
  formatJpy, formatNumber,
} from '../../../shared/utils/number';
import { RADIUS, SCOPE_TOKENS, TOKENS } from '../../../theme/muiTheme';
import useDataInputMaster from '../hooks/useDataInputMaster';
import {
  STRUCTURED_IMPORT_RESULT_STATUSES,
  validateScope3AfFiles,
} from '../model/dataInputActions';
import {
  ACTIVE_RECALCULATION_STATUSES,
  DATA_SOURCE_IDS,
  isDataInputRecoveryRequired,
  RECALCULATION_TARGET_BY_SOURCE_ID,
} from '../model/dataInputModel';
import { getImportResultPresentation } from '../model/importResultPresentation';
import mutationFeedback from '../model/mutationFeedback';
import { canOpenRecalculationDialog } from '../model/recalculationForm';
import UploadSourceCard, { DataSourceRow, HistoryButton } from './DataSourceCard';
import ImportResultDialog from './ImportResultDialog';
import OperationDialog from './OperationDialog';
import OutsourcingDialog from './OutsourcingDialog';
import RecalculationDialog from './RecalculationDialog';
import Scope3AfDeleteConfirmDialog from './Scope3AfDeleteConfirmDialog';
import Scope3AfSnapshotCard from './Scope3AfSnapshotCard';
import ScopeSection from './ScopeSection';
import SeatSyncDialog from './SeatSyncDialog';
import UploadConfirmDialog from './UploadConfirmDialog';
import UploadHistoryDialog from './UploadHistoryDialog';

const SOURCE_TONES = {
  fuel: SCOPE_TOKENS.SCOPE_1,
  electricity: SCOPE_TOKENS.SCOPE_2,
  afReport: SCOPE_TOKENS.SCOPE_3,
  ewaste: { color: TOKENS.error, bg: TOKENS.errorBg, dark: TOKENS.error },
  seat: { color: TOKENS.purple, bg: TOKENS.purpleBg, dark: TOKENS.purple },
  outsourcing: { color: TOKENS.teal, bg: TOKENS.tealBg, dark: TOKENS.teal },
  cat3: { color: TOKENS.indigo, bg: TOKENS.indigoBg, dark: TOKENS.indigo },
  cat8: { color: TOKENS.cyan, bg: TOKENS.cyanBg, dark: TOKENS.cyan },
};

const IMPORT_SOURCE_TITLE_KEYS = {
  [DATA_SOURCE_IDS.SCOPE_1_FUEL]: 'dataInput.source.fuel.title',
  [DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]: 'dataInput.source.electricity.title',
  [DATA_SOURCE_IDS.SCOPE_3_AF_REPORT]: 'dataInput.source.afReport.title',
  [DATA_SOURCE_IDS.SCOPE_3_EWASTE]: 'dataInput.source.ewaste.title',
};

const IMPORT_SOURCE_IDS = [
  DATA_SOURCE_IDS.SCOPE_1_FUEL,
  DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY,
  DATA_SOURCE_IDS.SCOPE_3_AF_REPORT,
  DATA_SOURCE_IDS.SCOPE_3_EWASTE,
];

const MESSAGE_TOKEN = /^[A-Z][A-Z0-9]*(?:[._-][A-Z0-9]+)*$/;

const hasStructuredImportResult = (operation) => Boolean(operation)
  && (
    STRUCTURED_IMPORT_RESULT_STATUSES.has(operation.status)
    || Boolean(operation.partialDataPossible)
  );

const firstImportFieldError = (actionError) => (
  Array.isArray(actionError?.fieldErrors) ? actionError.fieldErrors[0] || null : null
);

const safeActionErrorMessage = (actionError, fallback = null) => {
  const operationMessage = actionError?.operation?.message;
  if (typeof operationMessage === 'string' && operationMessage.trim()) {
    return operationMessage;
  }
  if (!(actionError instanceof ApiError)) return fallback;
  return firstImportFieldError(actionError)?.message || actionError.message || fallback;
};

function ValueChip({ icon, label, tone }) {
  return (
    <Chip
      icon={icon}
      label={label}
      size="small"
      sx={(theme) => ({
        bgcolor: alpha(tone.color, theme.palette.mode === 'dark' ? 0.13 : 0.055),
        border: 1,
        borderColor: alpha(tone.color, theme.palette.mode === 'dark' ? 0.28 : 0.16),
        color: 'text.primary',
        fontFamily: 'monospace',
        fontWeight: 650,
        '& .MuiChip-icon': { color: tone.color, fontSize: 16 },
      })}
    />
  );
}

function NoActionChip() {
  const { t } = useTranslation();
  return (
    <Chip
      icon={<CheckRoundedIcon />}
      label={t('dataInput.actions.noAction')}
      size="small"
      sx={(theme) => ({
        bgcolor: alpha(TOKENS.success, theme.palette.mode === 'dark' ? 0.12 : 0.055),
        border: 1,
        borderColor: alpha(TOKENS.success, 0.18),
        color: 'text.secondary',
        '& .MuiChip-icon': { color: TOKENS.success },
      })}
    />
  );
}

export default function DataInputPage() {
  const { t } = useTranslation();
  const importStatusUnavailableMessage = t('dataInput.importDialog.statusUnavailable');
  const { isAdmin } = useRole();
  const {
    years,
    year,
    setYear,
    data,
    loading,
    error,
    reload,
    loadImportHistory,
     loadDriverHistory,
     loadRecalculationReadiness,
    pendingFor,
    setPendingFile,
    uploadSource,
    scope3AfSnapshot,
    loadScope3AfSnapshot,
    submitScope3AfSnapshot,
    importMonitoringError: recoveredImportMonitoringError,
    recoveredImportResult,
    retryImportStatus,
    submitSeatDriver,
    submitCat8Driver,
    loadDriverSnapshot,
    saveOutsourcing,
    recalculate,
    recalculationOperation,
    recalculationMonitoringError,
    retryRecalculationStatus,
    isRecalculationProcessing,
    isSourceBusy,
    operationFor,
    scope1AttentionCount,
    scope2AttentionCount,
    scope3AttentionCount,
  } = useDataInputMaster();
  const [downloadingImportId, setDownloadingImportId] = useState(null);
  const downloadingImportRef = useRef(null);
  usePagePresentation({ loading: loading || Boolean(downloadingImportId) });
  const [toast, setToast] = useState(null);
  const [fileErrors, setFileErrors] = useState({});
  const [history, setHistory] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [outsourcingOpen, setOutsourcingOpen] = useState(false);
  const [recalculationOpen, setRecalculationOpen] = useState(false);
  const [recalculationReadinessLoading, setRecalculationReadinessLoading] = useState(false);
  const [seatSyncOpen, setSeatSyncOpen] = useState(false);
  const [cat8SyncOpen, setCat8SyncOpen] = useState(false);
  const [operationResult, setOperationResult] = useState(null);
  const [resultDialogSource, setResultDialogSource] = useState(null);
  const [uploadConfirmation, setUploadConfirmation] = useState(null);
  const [uploadConfirmationOpen, setUploadConfirmationOpen] = useState(false);
  const uploadConfirmationOpenRef = useRef(false);
  const [importDialogResult, setImportDialogResult] = useState(null);
  const [importDialogFeedback, setImportDialogFeedback] = useState(null);
  const [importMonitoringError, setImportMonitoringError] = useState(null);
  const [importMonitoringOperation, setImportMonitoringOperation] = useState(null);
  const [isTrackedImportProcessing, setIsTrackedImportProcessing] = useState(false);
  const [isTrackedRecalculation, setIsTrackedRecalculation] = useState(false);
  const handledImportIdsRef = useRef(new Set());
  const [scope3AfLocalFiles, setScope3AfLocalFiles] = useState([]);
  const [scope3AfRemovedRetainedIds, setScope3AfRemovedRetainedIds] = useState([]);
  const [scope3AfSnapshotChanged, setScope3AfSnapshotChanged] = useState(false);
  const [scope3AfSnapshotError, setScope3AfSnapshotError] = useState(false);
  const [scope3AfLoading, setScope3AfLoading] = useState(false);
  const [scope3AfDeleteTarget, setScope3AfDeleteTarget] = useState(null);
  const scope3AfLocalSeqRef = useRef(0);

  useEffect(() => {
    setFileErrors({});
  }, [year]);

  useEffect(() => {
    setScope3AfLocalFiles([]);
    setScope3AfRemovedRetainedIds([]);
    setScope3AfSnapshotChanged(false);
    setScope3AfSnapshotError(false);
    if (!isAdmin) return undefined;
    let cancelled = false;
    setScope3AfLoading(true);
    loadScope3AfSnapshot(year)
      .catch(() => {
        if (!cancelled) setScope3AfSnapshotError(true);
      })
      .finally(() => {
        if (!cancelled) setScope3AfLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, loadScope3AfSnapshot, year]);

  useEffect(() => {
    if (!isRecalculationProcessing) setIsTrackedRecalculation(false);
  }, [isRecalculationProcessing]);

  useEffect(() => {
    if (!recoveredImportMonitoringError) return;
    setIsTrackedImportProcessing(true);
    setImportMonitoringError(recoveredImportMonitoringError);
    setImportMonitoringOperation(recoveredImportMonitoringError.operation || null);
    if (!uploadConfirmationOpenRef.current) {
      setToast({
        severity: 'warning',
        message: importStatusUnavailableMessage,
      });
    }
  }, [importStatusUnavailableMessage, recoveredImportMonitoringError]);

  useBeforeUnloadWarning(
    isTrackedImportProcessing
    || (isTrackedRecalculation && isRecalculationProcessing),
  );

  const isRecoveryMode = isDataInputRecoveryRequired(error);
  const isHistoricalYear = isHistoricalViewerYear(year);
  const canManage = isAdmin;
  const canMutate = isAdmin && !error && !isHistoricalYear;

  const source = (sourceId) => data.sources[sourceId];
  const importOperationFor = (sourceId) => (
    operationFor(sourceId)
    || (importMonitoringOperation?.importType === sourceId ? importMonitoringOperation : null)
  );
  const isImportSourceBusy = (sourceId) => (
    isSourceBusy(sourceId)
    || (isTrackedImportProcessing && importMonitoringOperation?.importType === sourceId)
  );
  const isAnyUploadOrSyncBusy = isTrackedImportProcessing
    || IMPORT_SOURCE_IDS.some(isImportSourceBusy)
    || isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_SEAT_API)
    || isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_CAT8);
  const isUploadLocked = (sourceId) => (
    isAnyUploadOrSyncBusy && !isImportSourceBusy(sourceId)
  );
  const openHistory = async (title, sourceId, driverType = null) => {
    setHistory({ title, sourceId, driverType });
    setHistoryOpen(true);
    if (sourceId === DATA_SOURCE_IDS.SCOPE_1_FUEL
      || sourceId === DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY
      || sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT
      || sourceId === DATA_SOURCE_IDS.SCOPE_3_EWASTE) {
      try {
        const historySources = await loadImportHistory(year, sourceId);
        if (historySources[sourceId]) {
          setHistory((current) => current && {
            ...current,
            entries: historySources[sourceId].history || [],
          });
        }
      } catch (historyError) {
        showToastError(historyError, 'dataInput.operation.unknownError');
      }
    } else if (sourceId === DATA_SOURCE_IDS.SCOPE_3_SEAT_API
      || sourceId === DATA_SOURCE_IDS.SCOPE_3_CAT8
      || sourceId === DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING) {
      try {
        const historySources = await loadDriverHistory(year);
        if (historySources[sourceId]) {
          setHistory((current) => current && {
            ...current,
            entries: historySources[sourceId].history || [],
          });
        }
      } catch (historyError) {
        showToastError(historyError, 'dataInput.operation.unknownError');
      }
    }
  };

  const showToastError = (actionError, fallbackKey = 'dataInput.operation.unknownError') => {
    const detailMessage = safeActionErrorMessage(actionError, t(fallbackKey));
    setToast({
      severity: 'error',
      message: translateMessage(
        detailMessage,
        actionError?.status ?? 500,
        { year, ...(actionError?.params || {}) },
      ),
    });
  };

  const localizeFeedback = useCallback((descriptor, fallbackKey) => {
    if (!descriptor) return null;
    const params = { year, ...(descriptor.params || {}) };
    let message = null;
    if (descriptor.messageKey) message = t(descriptor.messageKey, params);
    else if (descriptor.message) message = descriptor.message;
    else if (MESSAGE_TOKEN.test(descriptor.messageCode || '')) {
      message = translateMessage(descriptor.messageCode, 500, params);
    }
    return {
      severity: descriptor.severity || 'info',
      message: message || t(fallbackKey || 'dataInput.operation.unknownError', params),
    };
  }, [t, year]);

  const returnFieldErrorsToForm = (actionError) => {
    if (!Array.isArray(actionError?.fieldErrors) || actionError.fieldErrors.length === 0) {
      return false;
    }
    setToast(null);
    return true;
  };

  const ensureImportTargetAvailable = (sourceId) => {
    if (!isImportSourceBusy(sourceId)) return true;
    showToastError(new ApiError('IMPORT_IN_PROGRESS', {
      status: 409,
    }));
    return false;
  };

  const handleFileChange = (sourceId, file) => {
    const validation = setPendingFile(sourceId, file);
    setFileErrors((current) => {
      const next = { ...current };
      if (validation.valid) delete next[sourceId];
      else next[sourceId] = t(`dataInput.error.${validation.code}`);
      return next;
    });
    if (!validation.valid) setToast(null);
  };

  const isScope3AfSnapshotChanged = (actionError) => (
    actionError?.message === SCOPE3_AF_ERROR_TOKENS.SNAPSHOT_CHANGED
  );

  const isScope3AfSnapshotUnchanged = (actionError) => (
    actionError?.message === SCOPE3_AF_ERROR_TOKENS.SNAPSHOT_UNCHANGED
  );

  const handleScope3AfFileChange = (files) => {
    const picked = Array.isArray(files) ? files : [];
    if (picked.length === 0) return;
    const validation = validateScope3AfFiles([
      ...scope3AfLocalFiles.map((item) => item.file),
      ...picked,
    ]);
    if (!validation.valid) {
      setToast({
        severity: 'error',
        message: t('dataInput.error.FILE_TOO_LARGE'),
      });
      return;
    }
    const newItems = picked.map((file) => ({
      key: `local-${scope3AfLocalSeqRef.current += 1}`,
      file,
    }));
    setScope3AfLocalFiles((previous) => [...newItems, ...previous]);
    setScope3AfSnapshotError(false);
    setToast(null);
  };

  const removeScope3AfFile = (key) => {
    if (String(key).startsWith('local-')) {
      setScope3AfLocalFiles((previous) => previous.filter((item) => item.key !== key));
      return;
    }
    if (String(key).startsWith('retained-')) {
      const retainedId = String(key).slice('retained-'.length);
      setScope3AfRemovedRetainedIds((previous) => (
        previous.includes(retainedId) ? previous : [...previous, retainedId]
      ));
    }
  };

  const handleScope3AfRemoveFile = (key) => {
    const file = scope3AfWorkingFiles.find((item) => item.key === key);
    setScope3AfDeleteTarget({ key, name: file?.name || '' });
  };

  const confirmScope3AfRemoveFile = () => {
    if (!scope3AfDeleteTarget) return;
    removeScope3AfFile(scope3AfDeleteTarget.key);
    setScope3AfDeleteTarget(null);
  };

  const refreshScope3AfAfterConflict = async () => {
    setScope3AfSnapshotChanged(true);
    setScope3AfRemovedRetainedIds([]);
    setIsTrackedImportProcessing(false);
    try {
      await loadScope3AfSnapshot(year);
    } catch {
      setScope3AfSnapshotError(true);
    }
  };

  const confirmScope3AfUpload = () => {
    if (!scope3AfIsDirty) {
      setToast({
        severity: 'info',
        message: t('dataInput.scope3Af.unchanged'),
      });
      return;
    }
    if (!ensureImportTargetAvailable(DATA_SOURCE_IDS.SCOPE_3_AF_REPORT)) return;
    resetImportDialogResult();
    setScope3AfSnapshotChanged(false);
    setUploadConfirmation({
      sourceId: DATA_SOURCE_IDS.SCOPE_3_AF_REPORT,
      title: afReportTitle,
      year,
    });
    setImportDialogOpen(true);
  };

  const sourceImportEnabled = (sourceId) => {
    const sourceState = source(sourceId);
    const allowedByBackend = sourceState.canImport;
    // Missing/unknown EF must not block a structurally valid zero-included import;
    // backend preflight remains authoritative when included rows require an EF.
    return canMutate
      && !isUploadLocked(sourceId)
      && allowedByBackend !== false;
  };

  const handleCurrentFileDownload = async (sourceId) => {
    const currentSource = source(sourceId);
    const importId = currentSource.currentImportId;
    if (!importId || !currentSource.currentDownloadAvailable || downloadingImportRef.current) return;

    downloadingImportRef.current = importId;
    setDownloadingImportId(importId);
    setToast(null);
    try {
      const payload = await createDataImportDownloadLink(importId);
      triggerBrowserDownload({
        href: payload.download_url,
        fileName: payload.file_name || currentSource.currentName || '',
      });
    } catch (actionError) {
      setToast({
        severity: 'error',
        message: translateMessage(
          safeActionErrorMessage(actionError),
          actionError?.status,
          actionError?.params,
        ),
      });
    } finally {
      if (downloadingImportRef.current === importId) downloadingImportRef.current = null;
      setDownloadingImportId((current) => (current === importId ? null : current));
    }
  };

  const setImportDialogOpen = useCallback((nextOpen) => {
    uploadConfirmationOpenRef.current = nextOpen;
    setUploadConfirmationOpen(nextOpen);
  }, []);

  const resetImportDialogResult = useCallback(() => {
    setImportDialogResult(null);
    setImportDialogFeedback(null);
    setImportMonitoringError(null);
    setImportMonitoringOperation(null);
  }, []);

  const hasCalculatedResultsFor = (sourceId) => Boolean(
    data.calculatedResults?.[RECALCULATION_TARGET_BY_SOURCE_ID[sourceId]],
  );

  const handleReportingYearChange = (nextYear) => {
    setImportDialogOpen(false);
    setIsTrackedImportProcessing(false);
    setUploadConfirmation(null);
    setScope3AfSnapshotChanged(false);
    setResultDialogSource(null);
    resetImportDialogResult();
    setYear(nextYear);
  };

  const feedbackForImportOperation = useCallback((operation) => {
    const descriptor = getImportResultPresentation(operation);
    return localizeFeedback({
      ...descriptor,
      params: {
        year: operation?.fiscalYear || year,
        ...(descriptor?.params || {}),
      },
    }, operation?.status === 'SUCCESS'
      ? 'common.feedback.import.success'
      : 'common.feedback.import.failure');
  }, [localizeFeedback, year]);

  const presentTerminalImport = useCallback((
    operation,
    { context = null, forceDialog = false } = {},
  ) => {
    const operationId = operation?.id;
    if (operationId && handledImportIdsRef.current.has(operationId)) return;
    if (operationId) handledImportIdsRef.current.add(operationId);

    const feedback = feedbackForImportOperation(operation);
    const isPartial = operation?.status === 'PARTIAL'
      || Boolean(operation?.partialDataPossible);
    const resultContext = context || uploadConfirmation || {
      sourceId: operation?.importType || null,
      title: IMPORT_SOURCE_TITLE_KEYS[operation?.importType]
        ? t(IMPORT_SOURCE_TITLE_KEYS[operation.importType])
        : operation?.importType || t('dataInput.importLock.unknownSource'),
      year: operation?.fiscalYear || year,
    };
    setImportMonitoringError(null);
    setImportMonitoringOperation(null);
    if (uploadConfirmationOpenRef.current || forceDialog || isPartial) {
      setToast(null);
      setUploadConfirmation(resultContext);
      setImportDialogFeedback(feedback);
      setImportDialogResult(operation);
      setIsTrackedImportProcessing(false);
      if (!uploadConfirmationOpenRef.current) setImportDialogOpen(true);
      return;
    }
    setToast(feedback);
    setUploadConfirmation(null);
    resetImportDialogResult();
    setIsTrackedImportProcessing(false);
  }, [
    feedbackForImportOperation,
    resetImportDialogResult,
    setImportDialogOpen,
    t,
    uploadConfirmation,
    year,
  ]);

  const presentImportRequestError = (actionError) => {
    setImportDialogResult(null);
    setImportMonitoringError(null);
    setImportMonitoringOperation(null);

    const fieldError = firstImportFieldError(actionError);
    if (fieldError?.message) {
      setToast(null);
      setImportDialogFeedback({
        severity: 'error',
        message: translateMessage(
          fieldError.message,
          actionError?.status ?? 400,
          { year, ...(actionError?.params || {}), ...(fieldError.params || {}) },
        ),
      });
      setIsTrackedImportProcessing(false);
      return;
    }

    setImportDialogFeedback(null);
    showToastError(actionError, 'common.feedback.import.failure');
    setIsTrackedImportProcessing(false);
  };

  const presentImportError = (actionError, context) => {
    const operation = actionError?.operation;
    if (!hasStructuredImportResult(operation)) {
      presentImportRequestError(actionError);
      return;
    }

    const message = safeActionErrorMessage(actionError, 'IMPORT_PROCESSING_FAILED');
    const resultOperation = {
      ...operation,
      fiscalYear: operation.fiscalYear || context?.year || year,
      importType: operation.importType || context?.sourceId || null,
      fileName: operation.fileName
        || (context?.sourceId ? pendingFor(context.sourceId)?.name : null),
      message,
      params: operation.params || actionError?.params || {},
      counters: operation.counters || {},
      partialDataPossible: Boolean(operation.partialDataPossible),
    };
    presentTerminalImport(resultOperation, { context });
  };

  const applyImportMonitorResult = (monitorResult, context = null) => {
    if (!monitorResult || monitorResult.kind === 'stale') return;
    if (monitorResult.kind === 'monitoring-error') {
      setImportMonitoringError(monitorResult.error);
      setImportMonitoringOperation(monitorResult.operation);
      if (!uploadConfirmationOpenRef.current) {
        setToast({
          severity: 'warning',
          message: t('dataInput.importDialog.statusUnavailable'),
        });
      }
      return;
    }
    presentTerminalImport(monitorResult.operation, { context });
  };

  const handleUpload = async (context) => {
    try {
      if (context.sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT) {
        const targetYear = Number(context.year || year);

        const result = await submitScope3AfSnapshot({
          fiscalYear: targetYear,
          expectedSnapshotId: scope3AfSnapshot.snapshotId || '',
          retainedFileIds: scope3AfRetainedIds,
          files: scope3AfLocalFiles.map((item) => item.file),
        });

        if (result?.kind === 'success') {
          // The submitted local files are now part of the published snapshot.
          // Clear draft state before rendering the refreshed retained file set.
          setScope3AfLocalFiles([]);
          setScope3AfRemovedRetainedIds([]);
          setScope3AfSnapshotChanged(false);
          setScope3AfSnapshotError(false);

          try {
            await loadScope3AfSnapshot(targetYear);
          } catch {
            setScope3AfSnapshotError(true);
          }
        }

        applyImportMonitorResult(result, context);
        return;
      }

      applyImportMonitorResult(await uploadSource(context.sourceId), context);
    } catch (actionError) {
      if (isScope3AfSnapshotChanged(actionError)) {
        await refreshScope3AfAfterConflict();
        return;
      }
      if (isScope3AfSnapshotUnchanged(actionError)) {
        setIsTrackedImportProcessing(false);
        setImportDialogOpen(false);
        setToast({
          severity: 'info',
          message: translateMessage(
            SCOPE3_AF_ERROR_TOKENS.SNAPSHOT_UNCHANGED,
            actionError?.status ?? 409,
          ),
        });
        return;
      }

      presentImportError(actionError, context);
    }
  };

  const confirmUpload = (sourceId, title) => {
    if (!ensureImportTargetAvailable(sourceId)) return;
    resetImportDialogResult();
    setUploadConfirmation({ sourceId, title, year });
    setImportDialogOpen(true);
  };

  const handleConfirmedUpload = () => {
    if (!uploadConfirmation) return;
    if (isImportSourceBusy(uploadConfirmation.sourceId)) {
      presentImportRequestError(new ApiError('IMPORT_IN_PROGRESS', { status: 409 }));
      return;
    }
    setIsTrackedImportProcessing(true);
    resetImportDialogResult();
    handleUpload(uploadConfirmation);
  };

  const monitorKnownImport = async (operation, context) => {
    try {
      applyImportMonitorResult(await retryImportStatus(operation), context);
    } catch (actionError) {
      if (hasStructuredImportResult(actionError?.operation)) {
        presentImportError(actionError, context);
        return;
      }
      applyImportMonitorResult({
        kind: 'monitoring-error',
        operation,
        error: actionError,
      }, context);
    }
  };

  const openActiveImportProgress = (sourceId, title) => {
    const operation = importOperationFor(sourceId);
    if (!operation) return;
    const monitoringStopped = Boolean(
      importMonitoringError
      && importMonitoringOperation?.id === operation.id,
    );
    const context = {
      sourceId,
      title,
      year: operation.fiscalYear || year,
    };
    if (!monitoringStopped) resetImportDialogResult();
    if (monitoringStopped) setToast(null);
    setUploadConfirmation(context);
    setIsTrackedImportProcessing(true);
    setImportDialogOpen(true);
    if (!monitoringStopped) monitorKnownImport(operation, context);
  };

  const handleRetryImportStatus = async () => {
    const operation = uploadConfirmation
      ? importOperationFor(uploadConfirmation.sourceId)
      : null;
    if (!operation?.id) return;
    setImportMonitoringError(null);
    await monitorKnownImport(operation, uploadConfirmation);
  };

  useEffect(() => {
    if (!recoveredImportResult) return;
    presentTerminalImport(recoveredImportResult, { forceDialog: true });
  }, [presentTerminalImport, recoveredImportResult]);

  const closeImportDialog = () => setImportDialogOpen(false);

  const reviewPartialImportHistory = () => {
    const target = uploadConfirmation;
    if (!target?.sourceId) return;
    setImportDialogOpen(false);
    openHistory(target.title, target.sourceId);
  };

  const refreshPartialImportData = async () => {
    await reload(uploadConfirmation?.year || year);
    setImportDialogOpen(false);
  };

  const handleImportDialogExited = () => {
    if (isTrackedImportProcessing) return;
    setUploadConfirmation(null);
    setScope3AfSnapshotChanged(false);
    resetImportDialogResult();
  };

  const handleSeatSync = async (payload) => {
    const action = payload.mode === 'sync' ? 'sync' : 'update';
    try {
      const result = await submitSeatDriver(payload);
      setSeatSyncOpen(false);
      setToast(localizeFeedback(mutationFeedback(result, {
        successMessageKey: `common.feedback.${action}.success`,
      })));
    } catch (actionError) {
      if (returnFieldErrorsToForm(actionError)) throw actionError;
      showToastError(actionError, `common.feedback.${action}.failure`);
    }
  };

  const handleCat8Sync = async (payload) => {
    const action = payload.mode === 'sync' ? 'sync' : 'update';
    try {
      const result = await submitCat8Driver(payload);
      setCat8SyncOpen(false);
      setToast(localizeFeedback(mutationFeedback(result, {
        successMessageKey: `common.feedback.${action}.success`,
      })));
    } catch (actionError) {
      if (returnFieldErrorsToForm(actionError)) throw actionError;
      showToastError(actionError, `common.feedback.${action}.failure`);
    }
  };

  const handleOutsourcingSave = async (payload) => {
    try {
      const result = await saveOutsourcing(payload);
      setOutsourcingOpen(false);
      setToast(localizeFeedback(mutationFeedback(result, {
        successMessageKey: 'common.feedback.update.success',
      })));
    } catch (actionError) {
      if (returnFieldErrorsToForm(actionError)) throw actionError;
      showToastError(actionError, 'common.feedback.update.failure');
    }
  };

  const handleRecalculate = async (payload) => {
    setIsTrackedRecalculation(true);
    try {
      const completed = await recalculate(payload);
      if (!completed) return;
      await loadRecalculationReadiness(year);
      setRecalculationOpen(false);
      setToast({
        severity: 'success',
        message: t(hasAnyCalculatedResult
          ? 'dataInput.toast.recalculated'
          : 'dataInput.toast.calculated', { year }),
      });
    } catch (actionError) {
      setIsTrackedRecalculation(false);
      if (returnFieldErrorsToForm(actionError)) throw actionError;
      showToastError(actionError);
    }
  };

  const handleRetryRecalculationStatus = async () => {
    try {
      const completed = await retryRecalculationStatus();
      if (!completed) return;
      if (completed.status === 'FAILED') {
        showToastError({
          operation: completed,
          message: completed.message,
        });
        return;
      }
      setToast({
        severity: 'success',
        message: t(hasAnyCalculatedResult
          ? 'dataInput.toast.recalculated'
          : 'dataInput.toast.calculated', { year }),
      });
    } catch (actionError) {
      showToastError(actionError);
    }
  };

  const fuelTitle = t('dataInput.source.fuel.title');
  const electricityTitle = t('dataInput.source.electricity.title');
  const afReportTitle = t('dataInput.source.afReport.title');
  const ewasteTitle = t('dataInput.source.ewaste.title');
  const seatTitle = t('dataInput.source.seat.title');
  const outsourcingTitle = t('dataInput.source.outsourcing.title');
  const scope3AfRetainedIds = scope3AfSnapshot.files
    .filter((file) => !scope3AfRemovedRetainedIds.includes(file._id))
    .map((file) => file._id);
  const scope3AfWorkingFiles = [
    ...scope3AfLocalFiles.map((item) => ({
      key: item.key,
      name: item.file.name,
      size: item.file.size,
      isRetained: false,
    })),
    ...scope3AfSnapshot.files
      .filter((file) => !scope3AfRemovedRetainedIds.includes(file._id))
      .map((file) => ({
        key: `retained-${file._id}`,
        id: file._id,
        name: file.original_filename || t('dataInput.history.notAvailable'),
        isRetained: true,
      })),
  ];
  const scope3AfIsDirty = scope3AfLocalFiles.length > 0
    || scope3AfRemovedRetainedIds.length > 0;
  const importDialogOperation = uploadConfirmation
    ? importOperationFor(uploadConfirmation.sourceId)
    : null;
  const isImportDialogProcessing = isTrackedImportProcessing && !importDialogResult;
  const efPublished = data.efPublished;
  const seat = source(DATA_SOURCE_IDS.SCOPE_3_SEAT_API);
  const outsourcing = source(DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING);
  const cat3 = source(DATA_SOURCE_IDS.SCOPE_3_CAT3);
  const cat8 = source(DATA_SOURCE_IDS.SCOPE_3_CAT8);
  const canOpenRecalculation = !isHistoricalYear && canOpenRecalculationDialog(canMutate);
  // A reporting year that has never produced any calculated result should offer
  // "Calculate" instead of "Recalculate".
  const hasAnyCalculatedResult = Object.values(data.calculatedResults || {}).some(Boolean);
  const showRecalculationStatus = !recalculationOpen
    && Boolean(recalculationOperation)
    && ACTIVE_RECALCULATION_STATUSES.has(recalculationOperation.status);
  const canSyncSeat = canMutate
    && (seat.canSync ?? data.actionFlags.canSyncSeat) !== false;
  const canSyncCat8 = canMutate
    && (cat8.canSync ?? data.actionFlags.canSyncCat8) !== false;
  const canUseSeatInput = canMutate
    && (canSyncSeat || seat.canManualInput === true);
  const canUseCat8Input = canMutate
    && (canSyncCat8 || cat8.canManualInput === true);
  const canEditCat19 = canMutate
    && !isAnyUploadOrSyncBusy
    && (outsourcing.canEdit ?? data.actionFlags.canEditCat19 ?? efPublished);

  return (
    <Box
      component="section"
      aria-labelledby="data-input-title"
      className="page-stack"
      sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}
    >
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={2}
        >
          <Stack spacing={0.5} sx={{ minWidth: 0 }}>
            <Typography id="data-input-title" component="h1" variant="h6" fontWeight={700}>
              {t('dataInput.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13 }}>
              {t('dataInput.subtitle')}
            </Typography>
          </Stack>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            spacing={1.25}
            sx={{ flexShrink: 0, width: { xs: '100%', md: 'auto' } }}
          >
            <LabeledField
              label={t('dataInput.reportingYear')}
              htmlFor="data-input-year"
              sx={{ minWidth: { sm: 200 }, width: { xs: '100%', sm: 200 } }}
            >
              <Select
                id="data-input-year"
                size="small"
                value={year}
                onChange={(event) => handleReportingYearChange(Number(event.target.value))}
                inputProps={{ 'aria-label': t('dataInput.reportingYear') }}
              >
                {years.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}
              </Select>
            </LabeledField>
            {isAdmin && !error && (
              <Button
                variant="contained"
                color="warning"
                startIcon={isRecalculationProcessing
                  ? <CircularProgress color="inherit" size={15} />
                  : <CalculateOutlinedIcon />}
                 onClick={async () => {
                   setRecalculationReadinessLoading(true);
                   try {
                     const readiness = await loadRecalculationReadiness(year);
                     if (readiness) setRecalculationOpen(true);
                   } catch (actionError) {
                     showToastError(actionError);
                   } finally {
                     setRecalculationReadinessLoading(false);
                   }
                 }}
                 disabled={isRecalculationProcessing || recalculationReadinessLoading || !canOpenRecalculation}
                sx={{
                  flexShrink: 0,
                  minHeight: 40,
                  minWidth: { sm: 168 },
                  px: 2,
                  color: 'common.white',
                  whiteSpace: 'nowrap',
                  '& .MuiButton-startIcon': { ml: 0, mr: 0.75 },
                  '&:hover': { bgcolor: 'warning.dark', color: 'common.white' },
                }}
              >
                 {isRecalculationProcessing || recalculationReadinessLoading
                  ? t(hasAnyCalculatedResult
                    ? 'dataInput.recalculating'
                    : 'dataInput.calculating')
                  : t(hasAnyCalculatedResult
                    ? 'dataInput.recalculate'
                    : 'dataInput.calculate')}
              </Button>
            )}
          </Stack>
        </Stack>
      </Paper>
      <DataState loading={loading} error={error} onRetry={() => reload()} />

      {(!error || isRecoveryMode) && (
        <>
          {showRecalculationStatus && (
            <Alert
              role="status"
              severity={recalculationMonitoringError ? 'warning' : 'info'}
              action={recalculationMonitoringError ? (
                <Button color="inherit" size="small" onClick={handleRetryRecalculationStatus}>
                  {t('dataInput.recalculation.retryStatus')}
                </Button>
              ) : undefined}
            >
              {recalculationMonitoringError
                ? t('dataInput.recalculation.statusUnavailable')
                : t(`dataInput.recalculation.status.${recalculationOperation.status}`)}
            </Alert>
          )}

          <Grid container spacing={2} alignItems="stretch">
            <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
              <ScopeSection
                title={t('dataInput.scope.scope1')}
                icon={LocalFireDepartmentOutlinedIcon}
                tone={SCOPE_TOKENS.SCOPE_1}
                attentionCount={scope1AttentionCount}
                headerActions={(
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartRoundedIcon />}
                    onClick={() => setResultDialogSource(DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                    disabled={!hasCalculatedResultsFor(DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                  >
                    {t('dataInput.actions.viewResult')}
                  </Button>
                )}
                fillContent
                sx={{ width: '100%' }}
              >
                <UploadSourceCard
                  fillContent
                  title={fuelTitle}
                  icon={LocalGasStationOutlinedIcon}
                  tone={SOURCE_TONES.fuel}
                  source={source(DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                  pendingFile={pendingFor(DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                  fileError={fileErrors[DATA_SOURCE_IDS.SCOPE_1_FUEL]}
                  onFileChange={(file) => handleFileChange(DATA_SOURCE_IDS.SCOPE_1_FUEL, file)}
                  onUpload={() => confirmUpload(DATA_SOURCE_IDS.SCOPE_1_FUEL, fuelTitle)}
                  onViewProgress={() => openActiveImportProgress(
                    DATA_SOURCE_IDS.SCOPE_1_FUEL,
                    fuelTitle,
                  )}
                  onDownload={canManage && source(DATA_SOURCE_IDS.SCOPE_1_FUEL).currentDownloadAvailable
                    ? () => handleCurrentFileDownload(DATA_SOURCE_IDS.SCOPE_1_FUEL)
                    : undefined}
                  downloadDisabled={Boolean(downloadingImportId)}
                  onHistory={() => openHistory(fuelTitle, DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                  busy={isImportSourceBusy(DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                  disabled={!sourceImportEnabled(DATA_SOURCE_IDS.SCOPE_1_FUEL)}
                  canUpload={canMutate}
                />
              </ScopeSection>
            </Grid>

            <Grid item xs={12} lg={6} sx={{ display: 'flex' }}>
              <ScopeSection
                title={t('dataInput.scope.scope2')}
                icon={BoltOutlinedIcon}
                tone={SCOPE_TOKENS.SCOPE_2}
                attentionCount={scope2AttentionCount}
                headerActions={(
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartRoundedIcon />}
                    onClick={() => setResultDialogSource(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                    disabled={!hasCalculatedResultsFor(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                  >
                    {t('dataInput.actions.viewResult')}
                  </Button>
                )}
                fillContent
                sx={{ width: '100%' }}
              >
                <UploadSourceCard
                  fillContent
                  title={electricityTitle}
                  icon={BoltOutlinedIcon}
                  tone={SOURCE_TONES.electricity}
                  source={source(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                  pendingFile={pendingFor(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                  fileError={fileErrors[DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY]}
                  onFileChange={(file) => handleFileChange(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY, file)}
                  onUpload={() => confirmUpload(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY, electricityTitle)}
                  onViewProgress={() => openActiveImportProgress(
                    DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY,
                    electricityTitle,
                  )}
                  onDownload={canManage && source(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY).currentDownloadAvailable
                    ? () => handleCurrentFileDownload(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)
                    : undefined}
                  downloadDisabled={Boolean(downloadingImportId)}
                  onHistory={() => openHistory(electricityTitle, DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                  busy={isImportSourceBusy(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                  disabled={!sourceImportEnabled(DATA_SOURCE_IDS.SCOPE_2_ELECTRICITY)}
                  canUpload={canMutate}
                />
              </ScopeSection>
            </Grid>

            <Grid item xs={12}>
              <ScopeSection
                title={t('dataInput.scope.scope3')}
                icon={PublicOutlinedIcon}
                tone={SCOPE_TOKENS.SCOPE_3}
                attentionCount={scope3AttentionCount}
                headerActions={(
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<TableChartRoundedIcon />}
                    onClick={() => setResultDialogSource(DATA_SOURCE_IDS.SCOPE_3_AF_REPORT)}
                    disabled={!hasCalculatedResultsFor(DATA_SOURCE_IDS.SCOPE_3_AF_REPORT)}
                  >
                    {t('dataInput.actions.viewResult')}
                  </Button>
                )}
              >
                <Scope3AfSnapshotCard
                  title={afReportTitle}
                  icon={DescriptionOutlinedIcon}
                  tone={SOURCE_TONES.afReport}
                  files={scope3AfWorkingFiles}
                  loading={scope3AfLoading}
                  fileError={scope3AfSnapshotError ? t('dataInput.scope3Af.loadFailed') : undefined}
                  onFileChange={handleScope3AfFileChange}
                  onRemoveFile={handleScope3AfRemoveFile}
                  onUpload={confirmScope3AfUpload}
                  onViewProgress={() => openActiveImportProgress(
                    DATA_SOURCE_IDS.SCOPE_3_AF_REPORT,
                    afReportTitle,
                  )}
                  onHistory={() => openHistory(afReportTitle, DATA_SOURCE_IDS.SCOPE_3_AF_REPORT)}
                  busy={isImportSourceBusy(DATA_SOURCE_IDS.SCOPE_3_AF_REPORT)}
                  disabled={!sourceImportEnabled(DATA_SOURCE_IDS.SCOPE_3_AF_REPORT)}
                  canUpload={canMutate}
                  hasChanges={scope3AfIsDirty}
                />

                <Box
                  sx={(theme) => ({
                    bgcolor: 'background.paper',
                    border: 1,
                    borderColor: alpha(theme.palette.text.primary, 0.12),
                    borderRadius: `${RADIUS.control}px`,
                    overflow: 'hidden',
                  })}
                >
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                    justifyContent="space-between"
                    spacing={1}
                    sx={(theme) => ({
                      bgcolor: alpha(TOKENS.purple, theme.palette.mode === 'dark' ? 0.1 : 0.035),
                      borderLeft: 3,
                      borderColor: TOKENS.purple,
                      p: { xs: 1.5, sm: 2 },
                    })}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <DeleteSweepOutlinedIcon sx={{ color: TOKENS.purple }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 750 }}>{t('dataInput.cat5.title')}</Typography>
                      </Box>
                    </Stack>
                    {!source(DATA_SOURCE_IDS.SCOPE_3_EWASTE).currentName && (
                      <Chip
                        icon={<ErrorOutlineRoundedIcon />}
                        label={t('dataInput.status.attention', { count: 1 })}
                        size="small"
                        sx={{ bgcolor: 'background.paper', color: 'warning.main', '& .MuiChip-icon': { color: 'inherit' } }}
                      />
                    )}
                  </Stack>
                  <Stack
                    spacing={1.5}
                    sx={(theme) => ({
                      bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.035 : 0.018),
                      borderTop: 1,
                      borderColor: 'divider',
                      p: { xs: 1.5, sm: 2 },
                    })}
                  >
                    <UploadSourceCard
                      title={ewasteTitle}
                      icon={RecyclingOutlinedIcon}
                      tone={SOURCE_TONES.ewaste}
                      source={source(DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      pendingFile={pendingFor(DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      fileError={fileErrors[DATA_SOURCE_IDS.SCOPE_3_EWASTE]}
                      onFileChange={(file) => handleFileChange(DATA_SOURCE_IDS.SCOPE_3_EWASTE, file)}
                      onUpload={() => confirmUpload(DATA_SOURCE_IDS.SCOPE_3_EWASTE, ewasteTitle)}
                      onViewProgress={() => openActiveImportProgress(
                        DATA_SOURCE_IDS.SCOPE_3_EWASTE,
                        ewasteTitle,
                      )}
                      onViewResult={() => setResultDialogSource(DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      viewResultDisabled={!hasCalculatedResultsFor(DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      onDownload={canManage && source(DATA_SOURCE_IDS.SCOPE_3_EWASTE).currentDownloadAvailable
                        ? () => handleCurrentFileDownload(DATA_SOURCE_IDS.SCOPE_3_EWASTE)
                        : undefined}
                      downloadDisabled={Boolean(downloadingImportId)}
                      onHistory={() => openHistory(ewasteTitle, DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      busy={isImportSourceBusy(DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      disabled={!sourceImportEnabled(DATA_SOURCE_IDS.SCOPE_3_EWASTE)}
                      canUpload={canMutate}
                    />
                    <DataSourceRow
                      title={seatTitle}
                      description={t('dataInput.source.seat.subtitle')}
                      icon={CloudSyncOutlinedIcon}
                      tone={SOURCE_TONES.seat}
                      actions={(
                        <>
                          {canMutate && (
                            <Tooltip
                              title={!canUseSeatInput ? t('dataInput.actions.syncCurrentYearOnly') : ''}
                              arrow
                            >
                              <span style={{ display: 'inline-flex' }}>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_SEAT_API)
                                    ? <CircularProgress size={14} />
                                    : <SyncRoundedIcon />}
                                  onClick={() => setSeatSyncOpen(true)}
                                  disabled={!canUseSeatInput
                                    || isAnyUploadOrSyncBusy
                                    || isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_SEAT_API)}
                                >
                                  {isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_SEAT_API)
                                    ? t('dataInput.actions.syncing')
                                    : t('dataInput.actions.sync')}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          <HistoryButton onClick={() => openHistory(
                            seatTitle, DATA_SOURCE_IDS.SCOPE_3_SEAT_API, 'SEAT',
                          )} />
                        </>
                      )}
                    >
                      <ValueChip
                        icon={<CloudSyncOutlinedIcon />}
                         label={seat.quarter
                           ? `Q${seat.quarter} · ${formatNumber(seat.totalSeats, { max: 0 })} seats`
                           : t('dataInput.source.noData')}
                        tone={SOURCE_TONES.seat}
                      />
                      {seat.workingDays != null && (
                        <ValueChip
                          icon={<CalculateOutlinedIcon />}
                          label={t('dataInput.quarterlyStatus.workingDays', {
                            count: seat.workingDays,
                          })}
                          tone={SOURCE_TONES.seat}
                        />
                      )}
                    </DataSourceRow>
                  </Stack>
                </Box>

                <DataSourceRow
                  title={outsourcingTitle}
                  description={outsourcing.calculationStatus !== 'EMPTY'
                    ? t('dataInput.source.outsourcing.updated', { date: formatDateTimeJst(outsourcing.updatedAt) })
                    : t('dataInput.source.outsourcing.empty', { year })}
                  icon={EditNoteOutlinedIcon}
                  tone={SOURCE_TONES.outsourcing}
                  actions={(
                    <>
                      {canMutate && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<EditOutlinedIcon />}
                          onClick={() => setOutsourcingOpen(true)}
                          disabled={!canEditCat19}
                        >
                          {t('dataInput.actions.edit')}
                        </Button>
                      )}
                      <HistoryButton onClick={() => openHistory(outsourcingTitle, DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING)} />
                    </>
                  )}
                >
                  <ValueChip
                    icon={<EditNoteOutlinedIcon />}
                    label={formatJpy(outsourcing.amountJpy)}
                    tone={SOURCE_TONES.outsourcing}
                  />
                  {outsourcing.calculationStatus === 'PENDING_EF' && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
                      {t('dataInput.outsourcing.pendingEf')}
                    </Typography>
                  )}
                </DataSourceRow>

                <DataSourceRow
                  title={t('dataInput.source.cat3.title')}
                  description={t('dataInput.source.cat3.subtitle')}
                  icon={BoltOutlinedIcon}
                  tone={SOURCE_TONES.cat3}
                  muted
                  actions={<NoActionChip />}
                >
                  <ValueChip
                    icon={<BoltOutlinedIcon />}
                     label={`${formatNumber(cat3.rawTotal, { max: 2 })} ${cat3.rawUnit || 'kWh'}`}
                    tone={SOURCE_TONES.cat3}
                  />
                </DataSourceRow>

                <DataSourceRow
                  title={t('dataInput.source.cat8.title')}
                  description={t('dataInput.source.cat8.subtitle')}
                  icon={ApartmentOutlinedIcon}
                  tone={SOURCE_TONES.cat8}
                  actions={(
                    <>
                      {canMutate && (
                        <Tooltip
                          title={!canUseCat8Input ? t('dataInput.actions.syncCurrentYearOnly') : ''}
                          arrow
                        >
                          <span style={{ display: 'inline-flex' }}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_CAT8)
                                ? <CircularProgress size={14} />
                                : <SyncRoundedIcon />}
                              onClick={() => setCat8SyncOpen(true)}
                              disabled={!canUseCat8Input
                                || isAnyUploadOrSyncBusy
                                || isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_CAT8)}
                            >
                              {isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_CAT8)
                                ? t('dataInput.actions.syncing')
                                : t('dataInput.actions.sync')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                      <HistoryButton onClick={() => openHistory(
                        t('dataInput.source.cat8.title'), DATA_SOURCE_IDS.SCOPE_3_CAT8, 'OFFICE_AREA',
                      )} />
                    </>
                  )}
                >
                  {cat8.quarter && (
                    <ValueChip
                      icon={<ApartmentOutlinedIcon />}
                      label={cat8.officeCount == null
                        ? t('dataInput.quarterlyStatus.totalArea', {
                          quarter: cat8.quarter,
                          area: formatNumber(cat8.totalAreaM2, { max: 2 }),
                        })
                        : t('dataInput.quarterlyStatus.officeArea', {
                          quarter: cat8.quarter,
                          area: formatNumber(cat8.totalAreaM2, { max: 2 }),
                          count: cat8.officeCount,
                        })}
                      tone={SOURCE_TONES.cat8}
                    />
                  )}
                </DataSourceRow>
              </ScopeSection>
            </Grid>
          </Grid>

          <OutsourcingDialog
            open={outsourcingOpen && canMutate}
            source={outsourcing}
            year={year}
            saving={isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_OUTSOURCING) || isAnyUploadOrSyncBusy}
            onClose={() => setOutsourcingOpen(false)}
            onSubmit={handleOutsourcingSave}
          />
          <RecalculationDialog
            open={recalculationOpen && canMutate}
            year={year}
            categories={data.recalculationImpact}
            running={isRecalculationProcessing}
            monitoringError={recalculationMonitoringError}
            hasAnyCalculatedResult={hasAnyCalculatedResult}
            onClose={() => setRecalculationOpen(false)}
            onSubmit={handleRecalculate}
            onRetryStatus={handleRetryRecalculationStatus}
          />
          <UploadHistoryDialog
            open={historyOpen}
            onClose={() => setHistoryOpen(false)}
            onExited={() => setHistory(null)}
            title={history?.title}
            entries={history?.entries || (history?.sourceId
              ? (source(history.sourceId)?.history || [])
              : [])}
            onLoadDetail={history?.driverType ? loadDriverSnapshot : undefined}
            canDownload={canManage}
            onDownload={createDataImportDownloadLink}
          />
          <Scope3AfDeleteConfirmDialog
            open={Boolean(scope3AfDeleteTarget)}
            fileName={scope3AfDeleteTarget?.name}
            onClose={() => setScope3AfDeleteTarget(null)}
            onConfirm={confirmScope3AfRemoveFile}
          />
          <UploadConfirmDialog
            open={uploadConfirmationOpen && (
              canMutate || Boolean(importDialogOperation || importDialogResult)
            )}
            year={uploadConfirmation?.year || year}
            sourceId={uploadConfirmation?.sourceId}
            sourceTitle={uploadConfirmation?.title}
            file={uploadConfirmation && !importDialogResult
              ? pendingFor(uploadConfirmation.sourceId)
              : null}
            currentName={uploadConfirmation ? source(uploadConfirmation.sourceId).currentName : ''}
            replacesYear={uploadConfirmation?.sourceId === DATA_SOURCE_IDS.SCOPE_3_EWASTE}
            snapshotMode={uploadConfirmation?.sourceId === DATA_SOURCE_IDS.SCOPE_3_AF_REPORT}
            snapshotChanged={scope3AfSnapshotChanged}
            files={scope3AfWorkingFiles}
            operation={importDialogOperation}
            result={importDialogResult}
            feedback={importDialogFeedback}
            isProcessing={isImportDialogProcessing}
            monitoringError={importMonitoringError}
            onClose={closeImportDialog}
            onExited={handleImportDialogExited}
            onConfirm={canMutate ? handleConfirmedUpload : undefined}
            onRetryStatus={handleRetryImportStatus}
            onReviewHistory={reviewPartialImportHistory}
            onRefreshData={refreshPartialImportData}
          />
          <ImportResultDialog
            open={Boolean(resultDialogSource)}
            sourceId={resultDialogSource}
            sourceTitle={resultDialogSource
              ? t(IMPORT_SOURCE_TITLE_KEYS[resultDialogSource])
              : ''}
            year={year}
            onClose={() => setResultDialogSource(null)}
            onExited={() => setResultDialogSource(null)}
          />
          <SeatSyncDialog
            open={seatSyncOpen && canMutate}
            year={year}
            source={seat}
            syncing={isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_SEAT_API)}
            onClose={() => setSeatSyncOpen(false)}
            onSubmit={handleSeatSync}
          />
          <SeatSyncDialog
            kind="cat8"
            open={cat8SyncOpen && canMutate}
            year={year}
            source={cat8}
            syncing={isSourceBusy(DATA_SOURCE_IDS.SCOPE_3_CAT8)}
            onClose={() => setCat8SyncOpen(false)}
            onSubmit={handleCat8Sync}
          />
          <OperationDialog value={operationResult} onClose={() => setOperationResult(null)} />
          <AppToast toast={toast} onClose={() => setToast(null)} />
        </>
      )}
    </Box>
  );
}
